import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { VerifyResponse, VerifyProcedure } from "../../types/app";
import { Card, Btn, Badge, PdfModal } from "../../components/ui";
import { API_BASE_URL } from "../../utils/api";
import { showGlobalToast } from "../../utils/globalNotify";
import SignaturePad from "../../components/common/SignaturePad";

type LoadState = "loading" | "loaded" | "neterror";

export function PublicVerify({ token }: { token: string }) {
  const [load, setLoad] = useState<LoadState>("loading");
  const [data, setData] = useState<VerifyResponse | null>(null);
  const [pdfView, setPdfView] = useState<{ name: string; url: string } | null>(null);
  const [sigBlob, setSigBlob] = useState<Blob | null>(null);
  const [hzBlob, setHzBlob] = useState<Blob | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchState = async () => {
    setLoad("loading");
    try {
      const resp = await fetch(`${API_BASE_URL}/api/v1/verify/${encodeURIComponent(token)}`, {
        cache: "no-store",
      });
      // 404 returns a JSON body with { state: "invalid" } as well
      const body = (await resp.json().catch(() => null)) as VerifyResponse | null;
      setData(body && body.state ? body : { state: "invalid" });
      setLoad("loaded");
    } catch {
      setLoad("neterror");
    }
  };

  useEffect(() => {
    void fetchState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const openPdf = (p: VerifyProcedure) => {
    if (!p.pdf_url) {
      showGlobalToast("Kein PDF hinterlegt.", "warning");
      return;
    }
    setPdfView({ name: p.pdf_name || p.title, url: p.pdf_url });
  };

  const closePdf = () => setPdfView(null);

  const confirm = async () => {
    if (!sigBlob || !hzBlob) {
      showGlobalToast("Bitte Unterschrift und Handzeichen zeichnen.", "warning");
      return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("signature", sigBlob, "unterschrift.png");
      fd.append("hand_sign", hzBlob, "handzeichen.png");
      const resp = await fetch(`${API_BASE_URL}/api/v1/verify/${encodeURIComponent(token)}/confirm`, {
        method: "POST",
        body: fd,
        cache: "no-store",
      });
      if (!resp.ok) {
        const err = (await resp.json().catch(() => null)) as { errors?: string[]; state?: string } | null;
        if (err?.state === "locked") {
          showGlobalToast("Diese Verfahrensanweisungen wurden bereits bestätigt.", "info");
          await fetchState();
        } else if (err?.state === "expired") {
          showGlobalToast("Der Link ist abgelaufen.", "error");
          await fetchState();
        } else {
          showGlobalToast(err?.errors?.[0] || "Bestätigung fehlgeschlagen.", "error");
        }
        return;
      }
      showGlobalToast("Bestätigung gespeichert. Vielen Dank!", "success");
      await fetchState();
    } catch {
      showGlobalToast("Netzwerkfehler. Bitte erneut versuchen.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const shell = (children: ReactNode) => (
    <div className="min-h-screen bg-gradient-to-b from-slate-100 via-white to-slate-200 text-slate-900">
      <div className="max-w-3xl mx-auto p-6 md:p-10">{children}</div>
    </div>
  );

  const centered = (text: string) =>
    shell(
      <div className="min-h-[60vh] flex items-center justify-center">
        <Card className="p-6 text-sm text-slate-700">{text}</Card>
      </div>
    );

  if (load === "loading") return centered("Wird geladen …");
  if (load === "neterror") return centered("Verbindung zum Server fehlgeschlagen. Bitte später erneut versuchen.");

  const state = data?.state ?? "invalid";
  if (state === "invalid") return centered("Link ungültig oder Mitarbeiter nicht gefunden.");
  if (state === "expired") return centered("Dieser Bestätigungslink ist abgelaufen. Bitte fordern Sie einen neuen Link an.");

  const procedures = data?.procedures || [];
  const locked = state === "locked";

  return shell(
    <Card className="p-6 grid gap-4">
      <div className="text-xl font-semibold">Prüfung Verfahrensanweisungen</div>
      {data?.employee_name && (
        <div className="text-sm text-slate-700">
          Mitarbeitende/r: <span className="font-medium">{data.employee_name}</span>
        </div>
      )}

      {locked && (
        <div className="rounded-lg bg-[var(--green-light)] border border-[var(--green)] px-3 py-2 text-sm text-[var(--green-dark)]">
          Bereits bestätigt
          {data?.confirmed_at ? ` am ${new Date(data.confirmed_at).toLocaleString("de-DE")}` : ""}.
        </div>
      )}

      <div className="text-sm">Zu bestätigende Verfahrensanweisungen ({procedures.length}):</div>
      <div className="grid gap-2">
        {procedures.length === 0 && <div className="text-sm text-slate-500">Keine VAs zugeordnet.</div>}
        {procedures.map((v) => (
          <div key={v.id} className="flex items-center gap-2">
            <div className="text-sm flex-1">
              • {v.title} <span className="text-xs text-slate-500">v{v.version}</span>
            </div>
            {v.pdf_url ? (
              <Btn variant="secondary" onClick={() => openPdf(v)}>Anzeigen</Btn>
            ) : (
              <span className="text-xs text-slate-500">kein PDF</span>
            )}
          </div>
        ))}
      </div>

      {!locked && (
        <>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <div className="text-sm font-medium mb-1">Unterschrift</div>
              {sigBlob ? (
                <div className="flex items-center gap-2">
                  <Badge>Unterschrift erfasst</Badge>
                  <Btn variant="secondary" onClick={() => setSigBlob(null)}>Neu zeichnen</Btn>
                </div>
              ) : (
                <SignaturePad onSave={(b) => setSigBlob(b)} />
              )}
            </div>
            <div>
              <div className="text-sm font-medium mb-1">Handzeichen</div>
              {hzBlob ? (
                <div className="flex items-center gap-2">
                  <Badge>Handzeichen erfasst</Badge>
                  <Btn variant="secondary" onClick={() => setHzBlob(null)}>Neu zeichnen</Btn>
                </div>
              ) : (
                <SignaturePad onSave={(b) => setHzBlob(b)} />
              )}
            </div>
          </div>

          <div className="flex items-center justify-end">
            <Btn onClick={confirm} disabled={submitting || !sigBlob || !hzBlob}>
              {submitting ? "Wird gespeichert …" : "Bestätigen"}
            </Btn>
          </div>
        </>
      )}

      {pdfView && <PdfModal name={pdfView.name} url={pdfView.url} onClose={closePdf} />}
    </Card>
  );
}
